import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const noteKeys = {
  byWorkspace: (workspaceSlug: string) => ['notes', 'workspace', workspaceSlug] as const,
  byProject: (workspaceSlug: string, projectSlug: string) =>
    ['notes', 'project', workspaceSlug, projectSlug] as const,
  byTask: (workspaceSlug: string, taskUlid: string) =>
    ['notes', 'task', workspaceSlug, taskUlid] as const,
  detail: (workspaceSlug: string, noteUlid: string) =>
    ['notes', 'detail', workspaceSlug, noteUlid] as const,
};

/**
 * Workspace-scoped notes feed (newest first, capped at 500). Powers the
 * workspace-level `/w/{slug}/notes` list page. Server returns notes from
 * both project- and task-scoped parents in a single payload.
 */
export const workspaceNotesQueryOptions = (api: ApiClient, workspaceSlug: string) =>
  queryOptions({
    queryKey: noteKeys.byWorkspace(workspaceSlug),
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/notes', {
        params: { path: { workspace_slug: workspaceSlug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useWorkspaceNotes = (workspaceSlug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...workspaceNotesQueryOptions(api, workspaceSlug ?? ''),
    enabled: Boolean(workspaceSlug),
  });
};

export const projectNotesQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  projectSlug: string,
) =>
  queryOptions({
    queryKey: noteKeys.byProject(workspaceSlug, projectSlug),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/notes',
        {
          params: {
            path: { workspace_slug: workspaceSlug, projectSlug },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useProjectNotes = (
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...projectNotesQueryOptions(api, workspaceSlug ?? '', projectSlug ?? ''),
    enabled: Boolean(workspaceSlug && projectSlug),
  });
};

export const taskNotesQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  taskUlid: string,
) =>
  queryOptions({
    queryKey: noteKeys.byTask(workspaceSlug, taskUlid),
    queryFn: async () => {
      const { data, error } = await api.GET(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/notes',
        {
          params: {
            path: { workspace_slug: workspaceSlug, taskUlid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
  });

export const useTaskNotes = (
  workspaceSlug: string | undefined,
  taskUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...taskNotesQueryOptions(api, workspaceSlug ?? '', taskUlid ?? ''),
    enabled: Boolean(workspaceSlug && taskUlid),
  });
};

export const noteQueryOptions = (
  api: ApiClient,
  workspaceSlug: string,
  noteUlid: string,
) =>
  queryOptions({
    queryKey: noteKeys.detail(workspaceSlug, noteUlid),
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/notes/{noteUlid}', {
        params: { path: { workspace_slug: workspaceSlug, noteUlid } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useNote = (
  workspaceSlug: string | undefined,
  noteUlid: string | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...noteQueryOptions(api, workspaceSlug ?? '', noteUlid ?? ''),
    enabled: Boolean(workspaceSlug && noteUlid),
  });
};

export interface CreateNoteInput {
  title: string;
  body?: string;
}

export const useCreateProjectNote = (workspaceSlug: string, projectSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/projects/{projectSlug}/notes',
        {
          params: { path: { workspace_slug: workspaceSlug, projectSlug } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: noteKeys.byProject(workspaceSlug, projectSlug) }),
        qc.invalidateQueries({ queryKey: noteKeys.byWorkspace(workspaceSlug) }),
      ]);
    },
  });
};

export const useCreateTaskNote = (workspaceSlug: string, taskUlid: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNoteInput) => {
      const { data, error } = await api.POST(
        '/v1/workspaces/{workspace_slug}/tasks/{taskUlid}/notes',
        {
          params: { path: { workspace_slug: workspaceSlug, taskUlid } },
          body: input,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: noteKeys.byTask(workspaceSlug, taskUlid) }),
        qc.invalidateQueries({ queryKey: noteKeys.byWorkspace(workspaceSlug) }),
      ]);
    },
  });
};

export interface UpdateNoteInput {
  title?: string;
  body?: string;
}

/**
 * Update a note's title and/or body. Per the locked pattern, the target
 * `noteUlid` is passed in the mutate input rather than at hook-factory
 * time — see `useReorderTask` for the rationale (avoids stale-closure
 * bugs when callers swap targets between renders).
 */
export const useUpdateNote = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateNoteInput & { noteUlid: string }) => {
      const { noteUlid, ...body } = input;
      const { data, error } = await api.PATCH(
        '/v1/workspaces/{workspace_slug}/notes/{noteUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, noteUlid } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        qc.invalidateQueries({
          queryKey: noteKeys.detail(workspaceSlug, variables.noteUlid),
        }),
        // Both list scopes can be affected — we don't know the parent
        // type from here without an extra round-trip, so invalidate
        // the family.
        qc.invalidateQueries({ queryKey: ['notes', 'project', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: ['notes', 'task', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: noteKeys.byWorkspace(workspaceSlug) }),
      ]);
    },
  });
};

export const useDeleteNote = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteUlid: string) => {
      const { error } = await api.DELETE(
        '/v1/workspaces/{workspace_slug}/notes/{noteUlid}',
        {
          params: { path: { workspace_slug: workspaceSlug, noteUlid } },
        },
      );
      if (error) throw error;
    },
    onSuccess: async (_data, noteUlid) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: noteKeys.detail(workspaceSlug, noteUlid) }),
        qc.invalidateQueries({ queryKey: ['notes', 'project', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: ['notes', 'task', workspaceSlug] }),
        qc.invalidateQueries({ queryKey: noteKeys.byWorkspace(workspaceSlug) }),
      ]);
    },
  });
};
